import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Alert as MuiAlert,
  Snackbar,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Container,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch
} from '@mui/material';
import axios from 'axios';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Fullscreen as FullscreenIcon,
  WaterDrop as WaterDropIcon,
  Speed as SpeedIcon,
  Thermostat as ThermostatIcon,
  Pool as PoolIcon,
  Person as PersonIcon,
  Opacity as OpacityIcon,
  Security as SecurityIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  Videocam as VideocamIcon,
  Notifications as NotificationsIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import api from '../services/api';
import { Pool, Alert, WaterQuality, SafetyStatus, Sensor, SensorData, ApiResponse } from '../types';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import CCTVFeed from '../components/cctv/CctvFeed';
import WearableStatus from '../components/WearableStatus';
import { CctvGridView } from '../components/cctv/CctvGridView';
import { Cctv } from '../types/cctv';
import RealCCTVTab from '../components/RealCCTVTab';
import PoolMonitoringAlertsTab from '../components/monitoring/PoolMonitoringAlertsTab';
import { useApiService } from '../hooks/useApiService';
import { ConnectionStatusIndicator, KymaBrandTitle } from '../components/common';
import Hls from 'hls.js';
import EventLogWidget from '../components/dashboard/EventLogWidget';
import SafetyAlertDispatchHistoryCard from '../components/safety/SafetyAlertDispatchHistoryCard';
import { usePlaybackConfig } from '../hooks/usePlaybackConfig';
import { pickCctvLivePlayback, resolveAbsoluteCctvStreamUrl } from '../utils/cctvStreamUrl';
import { preferHlsWorker, tryPlayMutedInline } from '../utils/hlsPlayback';
import Go2rtcWhepPlayer from '../components/cctv/Go2rtcWhepPlayer';
import { useAuth } from '../contexts/AuthContext';
import { useKeepGrantedTab } from '../brand/MenuPolicyContext';
import { MONITOR_TAB_IDS, hiddenTabProps } from '../brand/menuCatalog';
import {
  isPoolSiteStandaloneView,
  poolSiteMonitoringState,
  poolSiteShortPath,
  shouldRedirectStandaloneMonitoringToPoolEntry,
} from '../utils/poolSitePaths';
import { usePoolSitePageChrome } from '../hooks/usePoolSitePageChrome';
import { resolveSiteMonitorPlaybackFlags } from '../utils/siteMonitorPlayback';
import { fetchStreamJpegBlob, isRtspUrl } from '../utils/streamPreviewSnapshot';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pool-monitoring-tabpanel-${index}`}
      aria-labelledby={`pool-monitoring-tab-${index}`}
      style={{ display: value === index ? 'block' : 'none' }}
      {...other}
    >
      <Box sx={{ p: 3 }}>
        {children}
      </Box>
    </div>
  );
}

// 시설 코드로부터 시설 ID를 얻기 위한 함수
const getPoolIdFromCode = async (code: string): Promise<number | null> => {
  try {
    console.log(`[getPoolIdFromCode] 시설 코드 '${code}'에 대한 ID 조회 시작`);
    
    // 시설 정보 조회 API 사용
    const response = await axios.get(`/api/pools/code/${code}`);
    console.log('[getPoolIdFromCode] API 응답:', response.data);
    
    if (response.data && response.data.id) {
      const poolId = Number(response.data.id);
      console.log(`[getPoolIdFromCode] 시설 ID 찾음: ${poolId}`);
      return poolId;
    }
    
    console.warn('[getPoolIdFromCode] API 응답에서 ID를 찾을 수 없음');
    return null;
  } catch (error) {
    console.error('[getPoolIdFromCode] 오류 발생:', error);
    
    // 개발 환경에서 더미 ID 반환
    if (process.env.NODE_ENV === 'development') {
      console.log('[getPoolIdFromCode] 개발 환경 - 더미 ID 반환: 1');
      return 1;
    }
    
    return null;
  }
};

interface PoolMonitoringProps {
  poolId?: number | string;
}

/** 안전 상태 탭 CCTV 용도 매칭 (관제용 제외) */
const matchCctvPurpose = (purpose: string | null | undefined, labels: string[]) => {
  if (!purpose) return false;
  const p = String(purpose).trim();
  const pl = p.toLowerCase();
  return labels.some((label) => {
    const ll = label.toLowerCase();
    return p === label || p.includes(label) || pl === ll || pl.includes(ll);
  });
};

const CCTV_PURPOSE_SAFETY = ['안전 모니터링', 'safety monitoring'];
const CCTV_PURPOSE_MANAGEMENT = ['관리 모니터링', 'management monitoring'];
const CCTV_PURPOSE_OTHER = ['기타 모니터링', 'other monitoring'];

/** 안전 상태 탭 전용 썸네일 — WebRTC 주 / HLS 부 / 현장 RTSP 스냅샷 */
const SafetyCctvTile: React.FC<{
  cctv: Cctv;
  useRemotePlayback: boolean;
  siteGatewayEnabled: boolean;
  centralGo2rtcAvailable?: boolean;
  centralGo2rtcStreams?: string[];
  preferLocalRtspSnapshot?: boolean;
}> = ({
  cctv,
  useRemotePlayback,
  siteGatewayEnabled,
  centralGo2rtcAvailable = false,
  centralGo2rtcStreams = [],
  preferLocalRtspSnapshot = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const snapshotUrlRef = useRef<string | null>(null);
  const inputStream = (
    cctv.stream_url ||
    (cctv as { url?: string }).url ||
    ''
  ).trim();
  const useRtspSnapshot =
    preferLocalRtspSnapshot && Boolean(inputStream) && isRtspUrl(inputStream);
  const streamKey =
    cctv.pool_id && cctv.id ? `p${cctv.pool_id}_c${cctv.id}` : '';
  const live = pickCctvLivePlayback(
    {
      id: cctv.id,
      pool_id: cctv.pool_id,
      stream_url: cctv.stream_url,
      url: (cctv as { url?: string }).url,
      playback_url: (cctv as { playback_url?: string }).playback_url,
      webrtc_url: (cctv as { webrtc_url?: string }).webrtc_url,
    },
    useRemotePlayback,
    siteGatewayEnabled,
    centralGo2rtcAvailable,
    centralGo2rtcStreams
  );
  const [playbackMode, setPlaybackMode] = useState<'webrtc' | 'hls'>(() =>
    live.mode === 'webrtc' ? 'webrtc' : 'hls'
  );
  const [snapshotSrc, setSnapshotSrc] = useState<string | null>(null);
  const webrtcFallbackRef = useRef(false);

  useEffect(() => {
    webrtcFallbackRef.current = false;
    setPlaybackMode(live.mode === 'webrtc' ? 'webrtc' : 'hls');
  }, [streamKey]);

  // playback-config 최초 로드 후 WebRTC 가능해지면 승격 (폴링마다 모드 리셋은 하지 않음)
  useEffect(() => {
    if (live.mode === 'hls' && playbackMode === 'webrtc') {
      setPlaybackMode('hls');
    } else if (live.mode === 'webrtc' && playbackMode === 'hls' && !webrtcFallbackRef.current) {
      setPlaybackMode('webrtc');
    }
  }, [live.mode, playbackMode]);

  const handleWebrtcFallback = useCallback((_reason: string) => {
    webrtcFallbackRef.current = true;
    setPlaybackMode('hls');
  }, []);

  const hlsStreamSrc = useMemo(
    () =>
      live.hlsUrl ||
      resolveAbsoluteCctvStreamUrl(
        {
          id: cctv.id,
          pool_id: cctv.pool_id,
          stream_url: cctv.stream_url,
          url: (cctv as { url?: string }).url,
          playback_url: (cctv as { playback_url?: string }).playback_url,
        },
        useRemotePlayback,
        siteGatewayEnabled
      ),
    [
      live.hlsUrl,
      cctv.id,
      cctv.pool_id,
      cctv.stream_url,
      (cctv as { url?: string }).url,
      (cctv as { playback_url?: string }).playback_url,
      useRemotePlayback,
      siteGatewayEnabled,
    ]
  );

  useEffect(() => {
    if (!useRtspSnapshot || !inputStream) return;
    let cancelled = false;
    const clearSnap = () => {
      if (snapshotUrlRef.current) {
        URL.revokeObjectURL(snapshotUrlRef.current);
        snapshotUrlRef.current = null;
      }
    };
    const load = async () => {
      try {
        const blob = await fetchStreamJpegBlob(inputStream);
        if (cancelled) return;
        clearSnap();
        const obj = URL.createObjectURL(blob);
        snapshotUrlRef.current = obj;
        setSnapshotSrc(obj);
      } catch (err) {
        console.error(`CCTV ${cctv.id} RTSP 스냅샷 실패:`, err);
      }
    };
    void load();
    const timer = window.setInterval(() => {
      void load();
    }, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      clearSnap();
    };
  }, [useRtspSnapshot, inputStream, cctv.id]);

  useEffect(() => {
    if (useRtspSnapshot || playbackMode !== 'hls') return;
    const video = videoRef.current;
    if (!video || !hlsStreamSrc) {
      if (!hlsStreamSrc) {
        console.error(`CCTV ${cctv.id}: 재생 URL 없음`);
      }
      return;
    }

    let hls: Hls | null = null;
    let cancelled = false;

    if (Hls.isSupported()) {
      hls = new Hls({
        enableWorker: preferHlsWorker(),
        lowLatencyMode: false,
        liveSyncDurationCount: 3,
        backBufferLength: 30,
        maxBufferLength: 15,
        maxMaxBufferLength: 30,
        manifestLoadingMaxRetry: 4,
        levelLoadingMaxRetry: 4,
        fragLoadingMaxRetry: 4,
      });
      hls.loadSource(hlsStreamSrc);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (!cancelled) {
          void tryPlayMutedInline(video);
        }
      });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (!data.fatal || !hls) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
        else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsStreamSrc;
      void tryPlayMutedInline(video);
    }

    return () => {
      cancelled = true;
      if (hls) {
        hls.destroy();
      }
      video.removeAttribute('src');
      video.load();
    };
  }, [useRtspSnapshot, playbackMode, hlsStreamSrc, cctv.id]);

  return (
    <Box
      sx={{
        width: 'calc(33% - 4px)',
        height: 80,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      {useRtspSnapshot ? (
        snapshotSrc ? (
          <img
            src={snapshotSrc}
            alt={cctv.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={20} />
          </Box>
        )
      ) : playbackMode === 'webrtc' && live.webrtcUrl ? (
        <Go2rtcWhepPlayer
          whepUrl={live.webrtcUrl}
          muted
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onFallback={handleWebrtcFallback}
        />
      ) : (
        <video
          ref={videoRef}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          autoPlay
          muted
          playsInline
        />
      )}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          bottom: 4,
          left: 4,
          color: 'white',
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          px: 0.5,
          borderRadius: 0.5,
          zIndex: 1,
        }}
      >
        {cctv.name}
        {useRtspSnapshot ? ' · 현장' : playbackMode === 'webrtc' ? ' · WebRTC' : ' · HLS'}
      </Typography>
    </Box>
  );
};

const PoolMonitoring: React.FC<PoolMonitoringProps> = () => {
  const { poolApi, monitoringApi, sensorApi } = useApiService();
  const { useRemotePlayback, centralGo2rtcAvailable, centralGo2rtcStreams } = usePlaybackConfig();
  const { poolCode } = useParams<{ poolCode: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { authState } = useAuth();
  const isAdminLoggedIn = Boolean(
    authState.isAdminPasswordVerified && authState.isAuthenticated,
  );
  const siteStandalone = isPoolSiteStandaloneView(location.pathname, isAdminLoggedIn);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pool, setPool] = useState<Pool | null>(null);
  const siteMonitorFlags = resolveSiteMonitorPlaybackFlags({
    useRemotePlayback,
    siteGatewayEnabled: Boolean(
      (pool as Pool & { site_gateway_enabled?: boolean } | null)?.site_gateway_enabled
    ),
    siteMonitorPreferLocal: Boolean(
      (pool as Pool & { site_monitor_prefer_local?: boolean } | null)
        ?.site_monitor_prefer_local
    ),
    applySiteLocal: siteStandalone,
  });
  const [waterQuality, setWaterQuality] = useState<WaterQuality | null>(null);
  const [safetyStatus, setSafetyStatus] = useState<SafetyStatus | null>(null);
  /** 조회 완료 여부 — 없으면 스피너 대신 「없음」 표시 */
  const [waterQualityLoaded, setWaterQualityLoaded] = useState(false);
  const [safetyStatusLoaded, setSafetyStatusLoaded] = useState(false);
  const [waterQualityHistoryLoaded, setWaterQualityHistoryLoaded] = useState(false);
  const [safetyHistoryLoaded, setSafetyHistoryLoaded] = useState(false);
  
  // URL 쿼리에서 탭 정보 가져오기
  const queryParams = new URLSearchParams(location.search);
  const tabParam = queryParams.get('tab');
  const initialTab = tabParam ? parseInt(tabParam) : 0;
  
  const [currentTab, setCurrentTab] = useState<number>(initialTab);
  const monitorTabOn = useKeepGrantedTab(currentTab, MONITOR_TAB_IDS, setCurrentTab);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'addAlert' | 'editAlert' | 'waterQuality' | 'safetyCheck' | 'sensorSettings' | 'addCCTV' | 'addWearable'>('addAlert');
  const [selectedItem, setSelectedItem] = useState<Alert | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  usePoolSitePageChrome({
    poolName: pool?.name,
    poolCode,
    pageFallback: '시설 모니터링',
  });

  useEffect(() => {
    if (!siteStandalone || !poolCode) return;
    if (!shouldRedirectStandaloneMonitoringToPoolEntry(location.pathname, siteStandalone)) {
      return;
    }
    navigate(poolSiteShortPath(poolCode), {
      replace: true,
      state: poolSiteMonitoringState(),
    });
  }, [poolCode, location.pathname, siteStandalone, navigate]);

  // API 데이터 상태
  const [pools, setPools] = useState<Pool[]>([]);
  const [selectedPool, setSelectedPool] = useState<Pool | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [sensorData, setSensorData] = useState<SensorData[]>([]);
  const [selectedSensor, setSelectedSensor] = useState<Sensor | null>(null);
  const [selectedCamera, setSelectedCamera] = useState<number | null>(null);

  // 수질 히스토리 데이터
  const [waterQualityHistory, setWaterQualityHistory] = useState<WaterQuality[]>([]);
  
  // 안전 상태 히스토리 데이터
  const [safetyHistory, setSafetyHistory] = useState<SafetyStatus[]>([]);
  
  // 활성 알림 데이터
  const [activeAlerts, setActiveAlerts] = useState<Alert[]>([]);
  
  // 카메라 유형 상태
  const [cameraType, setCameraType] = useState<'all' | 'safety' | 'management'>('all');
  
  // 활성 웨어러블 상태
  const [wearables, setWearables] = useState<Wearable[]>([]);
  const [wearableLoading, setWearableLoading] = useState<boolean>(false);
  const [wearableError, setWearableError] = useState<string | null>(null);
  
  const [cctvs, setCctvs] = useState<Cctv[]>([]);
  const [cctvLoading, setCctvLoading] = useState<boolean>(false);
  const [cctvError, setCctvError] = useState<string | null>(null);
  const videoRefs = useRef<{ [key: number]: HTMLVideoElement | null }>({});
  const hlsInstances = useRef<{ [key: number]: Hls | null }>({});

  // 수질 데이터 가져오기
  const fetchWaterQuality = async (poolId: number) => {
    setWaterQualityLoaded(false);
    try {
      console.log(`[fetchWaterQuality] 시설 ID ${poolId}의 수질 데이터 조회 시작`);
      const response = await axios.get(`/api/pools/${poolId}/water-quality`);
      
      if (response.data.success && response.data.data) {
        console.log('[fetchWaterQuality] 수질 데이터 조회 성공:', response.data.data);
        setWaterQuality(response.data.data);
      } else {
        setWaterQuality(null);
      }
    } catch (error) {
      console.error('[fetchWaterQuality] 오류 발생:', error);
      setWaterQuality(null);
    } finally {
      setWaterQualityLoaded(true);
    }
  };

  // 안전 상태 데이터 가져오기
  const fetchSafetyStatus = async (poolId: number) => {
    setSafetyStatusLoaded(false);
    try {
      console.log(`[fetchSafetyStatus] 시설 ID ${poolId}의 안전 상태 조회 시작`);
      const response = await axios.get(`/api/pools/${poolId}/safety-status`);
      
      if (response.data.success && response.data.data) {
        console.log('[fetchSafetyStatus] 안전 상태 조회 성공:', response.data.data);
        setSafetyStatus(response.data.data);
      } else {
        setSafetyStatus(null);
      }
    } catch (error) {
      console.error('[fetchSafetyStatus] 오류 발생:', error);
      setSafetyStatus(null);
    } finally {
      setSafetyStatusLoaded(true);
    }
  };

  // 센서 데이터 가져오기
  const fetchSensors = async (poolId: number) => {
    try {
      console.log(`[fetchSensors] 시설 ID ${poolId}의 센서 데이터 조회 시작`);
      const response = await axios.get(`/api/pools/${poolId}/sensors`);
      
      if (response.data.success && response.data.data) {
        console.log('[fetchSensors] 센서 데이터 조회 성공:', response.data.data);
        setSensors(response.data.data);
        
        if (response.data.data.length > 0) {
          setSelectedSensor(response.data.data[0]);
        }
      } else {
        throw new Error(response.data.message || '센서 데이터를 가져오기 실패');
      }
    } catch (error) {
      console.error('[fetchSensors] 오류 발생:', error);
      
      // 개발 환경에서는 더미 데이터 사용
      if (process.env.NODE_ENV === 'development') {
        console.log('[fetchSensors] 개발 환경 - 더미 데이터 사용');
        const dummySensors: Sensor[] = [
          { 
            id: 201, pool_id: poolId, name: '수온 센서 (메인)', type: 'temperature', 
            location: '메인 풀 중앙', status: 'active', 
            last_reading: '27.8', last_reading_unit: '°C',
            created_at: new Date().toISOString(), updated_at: new Date().toISOString() 
          },
          { 
            id: 202, pool_id: poolId, name: 'pH 센서 (유아풀)', type: 'ph', 
            location: '유아풀 여과기 옆', status: 'active', 
            last_reading: '7.3', last_reading_unit: 'pH',
            created_at: new Date().toISOString(), updated_at: new Date().toISOString() 
          },
          { 
            id: 203, pool_id: poolId, name: '염소 센서 (메인)', type: 'chlorine', 
            location: '메인 풀 약품 투입구', status: 'inactive', 
            last_reading: '1.1', last_reading_unit: 'ppm',
            created_at: new Date().toISOString(), updated_at: new Date().toISOString() 
          }
        ];
        setSensors(dummySensors);
        
        if (dummySensors.length > 0) {
          setSelectedSensor(dummySensors[0]);
        }
      }
    }
  };

  // 알림 데이터 가져오기
  const fetchAlerts = async (poolId: number) => {
    try {
      console.log(`[fetchAlerts] 시설 ID ${poolId}의 알림 데이터 조회 시작`);
      const response = await axios.get(`/api/pools/${poolId}/alerts`);
      
      if (response.data.success && response.data.data) {
        console.log('[fetchAlerts] 알림 데이터 조회 성공:', response.data.data);
        setAlerts(response.data.data);
        setActiveAlerts(response.data.data.filter(a => a.status === 'active'));
      } else {
        throw new Error(response.data.message || '알림 데이터를 가져오기 실패');
      }
    } catch (error) {
      console.error('[fetchAlerts] 오류 발생:', error);
      
      // 개발 환경에서는 더미 데이터 사용
      if (process.env.NODE_ENV === 'development') {
        console.log('[fetchAlerts] 개발 환경 - 더미 데이터 사용');
        const dummyAlerts: Alert[] = [
          {
            id: 101, pool_id: poolId, type: 'water_quality', 
            title: '염소 수치 낮음 (더미)', message: '염소 수치가 권장 범위보다 낮음. 확인 필요', 
            severity: 'medium', status: 'active', 
            created_at: new Date(Date.now() - 1200000).toISOString(),
            updated_at: new Date(Date.now() - 1200000).toISOString()
          },
          {
            id: 102, pool_id: poolId, type: 'safety', 
            title: '혼잡도 높음 (더미)', message: '시설 이용객 수가 많아 혼잡', 
            severity: 'low', status: 'acknowledged', 
            created_at: new Date(Date.now() - 3600000).toISOString(),
            updated_at: new Date(Date.now() - 1800000).toISOString()
          },
          {
            id: 103, pool_id: poolId, type: 'equipment', 
            title: '펌프 점검 필요 (더미)', message: '주요 순환 펌프의 성능 저하가 감지됨', 
            severity: 'high', status: 'active', 
            created_at: new Date(Date.now() - 60000).toISOString(),
            updated_at: new Date(Date.now() - 60000).toISOString()
          }
        ];
        setAlerts(dummyAlerts);
        setActiveAlerts(dummyAlerts.filter(a => a.status === 'active'));
      }
    }
  };

  // 2. 데이터 로딩 useEffect 수정
  useEffect(() => {
    const fetchPoolData = async () => {
      if (!poolCode) {
        setError("시설 코드가 제공되지 않음");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        console.log(`[PoolMonitoring] 시설 정보 조회 시작 (코드: ${poolCode})`);
        
        // 시설 코드로부터 ID 얻기
        const poolId = await getPoolIdFromCode(poolCode);
        if (!poolId) {
          throw new Error(`시설 코드 '${poolCode}'에 해당하는 ID를 찾기 불가`);
        }
        
        console.log(`[PoolMonitoring] 시설 ID 조회 완료: ${poolId}`);
        
        // 시설 정보 가져오기
        const response = await axios.get(`/api/pools/code/${poolCode}`);
        if (!response.data) {
          throw new Error("시설 정보를 가져오기 실패");
        }
        
        console.log('[PoolMonitoring] 시설 정보 조회 완료:', response.data);
        setPool(response.data);
        
        // ID를 이용하여 수질, 안전성, 센서 데이터 등을 조회
        await Promise.all([
          fetchWaterQuality(poolId),
          fetchSafetyStatus(poolId),
          fetchSensors(poolId),
          fetchAlerts(poolId)
        ]);
        
        console.log('[PoolMonitoring] 모든 데이터 조회 완료');
      } catch (error) {
        console.error('[PoolMonitoring] 오류 발생:', error);
        setError(error.message || "데이터를 불러오는 중 오류 발생");
      } finally {
        setLoading(false);
      }
    };

    fetchPoolData();
    
    // 자동 갱신 제거
    return () => {
      // cleanup
    };
  }, [poolCode]);

  // 센서 데이터 생성 함수
  const generateSensorData = (sensor: Sensor) => {
    const data: SensorData[] = [];
    const now = new Date();
    
    // 지난 24시간의 데이터 생성
    for (let i = 0; i < 24; i++) {
      const timestamp = new Date(now);
      timestamp.setHours(now.getHours() - i);
      
      // 센서 유형에 따른 값 및 단위 설정
      let value = 0;
      let unit = '';
      
      switch (sensor.type) {
        case 'temperature':
          value = 25 + Math.random() * 3;
          unit = '°C';
          break;
        case 'ph':
          value = 7.2 + (Math.random() - 0.5) * 0.4;
          unit = 'pH';
          break;
        case 'chlorine':
          value = 1.5 + (Math.random() - 0.5) * 0.5;
          unit = 'ppm';
          break;
        case 'turbidity':
          value = 0.3 + Math.random() * 0.3;
          unit = 'NTU';
          break;
        default:
          value = Math.random() * 100;
          unit = '';
      }
      
      data.push({
        id: i + 1,
        sensor_id: sensor.id,
        value: value.toFixed(2),
        unit,
        timestamp: timestamp.toISOString(),
        created_at: timestamp.toISOString()   // SensorData 타입에 필요한 필드 추가
      });
    }
    
    setSensorData(data);
  };
  
  // 센서 클릭 핸들러
  const handleSensorClick = (sensor: Sensor) => {
    setSelectedSensor(sensor);
    generateSensorData(sensor);
  };

  // 더미 데이터 생성 함수
  const generateWaterQualityHistoryData = () => {
    const today = new Date();
    const history: WaterQuality[] = [];
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      // 현실적인 수질 변동을 시뮬레이션
      const baseChlorine = 1.5; // 기본 염소 수치 (ppm)
      const basePh = 7.4; // 기본 pH 수치
      const baseTemp = 27.5; // 기본 수온 (°C)
      const baseTurbidity = 0.4; // 기본 탁도 (NTU)
      
      // 약간의 무작위성 추가
      const randomFactor = (Math.random() - 0.5) * 2; // -1 ~ 1 사이의 랜덤 값
      
      history.push({
        id: i + 1,
        pool_id: pool?.id || 0,
        // 최근일수록 현재 측정값에 가까워지도록 조정
        chlorine: i === 0 ? (waterQuality?.chlorine || baseChlorine) :
                 baseChlorine + (randomFactor * 0.3),
        ph: i === 0 ? (waterQuality?.ph || basePh) :
           basePh + (randomFactor * 0.2),
        temperature: i === 0 ? (waterQuality?.temperature || baseTemp) :
                    baseTemp + (randomFactor * 1.0),
        turbidity: i === 0 ? (waterQuality?.turbidity || baseTurbidity) :
                  baseTurbidity + (randomFactor * 0.2),
        created_at: date.toISOString(),
        updated_at: date.toISOString(),
        lastCheck: date.toISOString()
      });
    }
    
    return history;
  };
  
  // 안전 상태 히스토리 더미 데이터 생성
  const generateSafetyHistoryData = () => {
    const today = new Date();
    const history: SafetyStatus[] = [];
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      // 현재 사용자 수에 무작위성 추가하여 변동 패턴 생성
      const currentCrowdLevel = i === 0 ? (safetyStatus?.crowd_level || 30) :
                             Math.floor(30 + (Math.random() * 20) * (6 - i) / 6);
      
      history.push({
        id: i + 1,
        pool_id: pool?.id || 0,
        lifeguard_count: i === 0 ? (safetyStatus?.lifeguard_count || 2) : // Use lifeguard_count
                         Math.max(1, Math.floor(2 + (Math.random() - 0.3))),
        requiredLifeguards: safetyStatus?.requiredLifeguards || 2, // Use correct name
        is_safe: true, // is_safe exists
        crowd_level: currentCrowdLevel, // Use number
        created_at: date.toISOString(),
        updated_at: date.toISOString(),
        status: 'normal', 
        timestamp: date.toISOString(),
        lifeguards: i === 0 ? (safetyStatus?.lifeguards || 2) : Math.max(1, Math.floor(2 + (Math.random() - 0.3))),
        emergencyEquipment: { aeds: 1, firstAidKits: 1, rescueTubes: 2 },
        emergency_equipment_count: 4
      }); // Removed 'as SafetyStatus'
    }
    
    return history;
  };
  
  // 히스토리: API 미연동 — 현재값도 없으면 빈 목록(없음 표시). 값이 있을 때만 데모 곡선.
  useEffect(() => {
    if (!pool?.id) {
      setWaterQualityHistory([]);
      setSafetyHistory([]);
      setWaterQualityHistoryLoaded(false);
      setSafetyHistoryLoaded(false);
      return;
    }
    if (waterQuality) {
      setWaterQualityHistory(generateWaterQualityHistoryData());
    } else if (waterQualityLoaded) {
      setWaterQualityHistory([]);
    }
    if (safetyStatus) {
      setSafetyHistory(generateSafetyHistoryData());
    } else if (safetyStatusLoaded) {
      setSafetyHistory([]);
    }
    if (waterQualityLoaded) setWaterQualityHistoryLoaded(true);
    if (safetyStatusLoaded) setSafetyHistoryLoaded(true);
  }, [pool, waterQuality, safetyStatus, waterQualityLoaded, safetyStatusLoaded]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleBackClick = () => {
    navigate('/central-control');
  };

  const fetchEquipmentData = useCallback(async () => {
    if (!poolCode) return;

    try {
      setLoading(true);
      const response = await axios.get(`/api/pools/code/${poolCode}/equipment`);
      if (response.data.success) {
        // 장비 데이터 처리
        console.log('[fetchEquipmentData] 장비 데이터 조회 성공:', response.data.data);
      } else {
        throw new Error(response.data.message || '장비 데이터를 가져오기 실패');
      }
    } catch (error) {
      console.error('[fetchEquipmentData] 오류 발생:', error);
      
      // 개발 환경에서는 더미 데이터 사용
      if (process.env.NODE_ENV === 'development') {
        console.log('[fetchEquipmentData] 개발 환경 - 더미 데이터 사용');
        // 더미 데이터 처리
      }
    } finally {
      setLoading(false);
    }
  }, [poolCode]);

  // HLS 스트림 초기화 함수
  const initializeHlsStream = useCallback((cctv: Cctv, videoElement: HTMLVideoElement) => {
    const rawStream = (cctv.stream_url || cctv.url || '').trim();
    if (
      siteMonitorFlags.preferLocalRtspSnapshot &&
      isRtspUrl(rawStream)
    ) {
      return;
    }
    const streamSrc = resolveAbsoluteCctvStreamUrl(
      {
        id: cctv.id,
        pool_id: cctv.pool_id,
        stream_url: cctv.stream_url,
        url: cctv.url,
        playback_url: cctv.playback_url,
      },
      siteMonitorFlags.useRemotePlayback,
      siteMonitorFlags.siteGatewayEnabled
    );
    if (!streamSrc) {
      console.error(`CCTV ${cctv.id}: 재생 URL 없음`);
      return;
    }

    if (hlsInstances.current[cctv.id]) {
      hlsInstances.current[cctv.id]?.destroy();
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: preferHlsWorker(),
        lowLatencyMode: true,
        backBufferLength: 90,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        maxBufferSize: 60 * 1000 * 1000,
        maxBufferHole: 0.5,
        abrEwmaDefaultEstimate: 500000,
        startLevel: -1,
        manifestLoadingTimeOut: 10000,
        manifestLoadingMaxRetry: 3,
        levelLoadingTimeOut: 10000,
        levelLoadingMaxRetry: 3,
        fragLoadingTimeOut: 20000,
        fragLoadingMaxRetry: 3
      });
      
      hls.loadSource(streamSrc);
      hls.attachMedia(videoElement);
      
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        void tryPlayMutedInline(videoElement);
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.error(`HLS 에러 발생 (CCTV ID: ${cctv.id}):`, data);
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              hls.destroy();
              break;
          }
        }
      });

      hlsInstances.current[cctv.id] = hls;
    } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
      videoElement.src = streamSrc;
      videoElement.addEventListener('loadedmetadata', () => {
        videoElement.play().catch(error => {
          console.error(`비디오 재생 실패 (CCTV ID: ${cctv.id}):`, error);
        });
      });
    }
  }, [
    siteMonitorFlags.useRemotePlayback,
    siteMonitorFlags.siteGatewayEnabled,
    siteMonitorFlags.preferLocalRtspSnapshot,
  ]);

  // CCTV 데이터 가져오기
  const fetchCctvs = useCallback(async () => {
    if (!pool?.id) return;
    
    try {
      setCctvLoading(true);
      setCctvError(null);
      
      const response = await axios.get(`/api/pools/${pool.id}/cctvs`);
      if (response.data && response.status >= 200 && response.status < 300) {
        const cctvData = response.data.data || response.data;
        if (Array.isArray(cctvData)) {
          setCctvs(cctvData);
        }
      }
    } catch (error) {
      console.error('CCTV 데이터 가져오기 실패:', error);
      setCctvError('CCTV 데이터를 가져오기 실패');
      
      // 개발 환경에서는 더미 데이터 사용
      if (process.env.NODE_ENV === 'development') {
        console.log('[fetchCctvs] 개발 환경 - 더미 데이터 사용');
        const dummyCctvs: Cctv[] = [
          {
            id: 1,
            name: '메인 풀 CCTV',
            stream_url: 'https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8',
            status: 'ACTIVE',
            location: '메인 풀',
            pool_id: Number(pool.id),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            code: 'CCTV001',
            is_active: true,
            purpose: '안전 모니터링'
          },
          {
            id: 2,
            name: '유아 풀 CCTV',
            stream_url: 'https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8',
            status: 'ACTIVE',
            location: '유아 풀',
            pool_id: Number(pool.id),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            code: 'CCTV002',
            is_active: true,
            purpose: '안전 모니터링'
          },
          {
            id: 3,
            name: '입구 CCTV',
            stream_url: 'https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8',
            status: 'ACTIVE',
            location: '입구',
            pool_id: Number(pool.id),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            code: 'CCTV003',
            is_active: true,
            purpose: '관리 모니터링'
          }
        ];
        setCctvs(dummyCctvs);
      }
    } finally {
      setCctvLoading(false);
    }
  }, [pool?.id]);

  // 컴포넌트 마운트 시 CCTV 데이터 가져오기
  useEffect(() => {
    fetchCctvs();
  }, [fetchCctvs]);

  // CCTV 영상 자동 재생을 위한 useEffect
  useEffect(() => {
    cctvs.forEach(cctv => {
      const videoElement = videoRefs.current[cctv.id];
      if (videoElement && cctv.stream_url) {
        initializeHlsStream(cctv, videoElement);
      }
    });

    // 컴포넌트 언마운트 시 HLS 인스턴스 정리
    return () => {
      Object.values(hlsInstances.current).forEach(hls => {
        if (hls) {
          hls.destroy();
        }
      });
    };
  }, [cctvs, initializeHlsStream]);

  // 탭 변경 시 CCTV 데이터 새로고침 (CCTV·안전 상태)
  useEffect(() => {
    if (currentTab === 1 || currentTab === 3) {
      fetchCctvs();
    }
  }, [currentTab, fetchCctvs]);

  return (
    <Container maxWidth="lg">
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ mt: 3 }}>
          <MuiAlert severity="error">{error}</MuiAlert>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              mt: 3,
              mb: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexWrap: 'wrap',
              gap: { xs: 1, sm: 2 },
              minWidth: 0,
            }}
          >
            <KymaBrandTitle variant="central" tone="default" size="medium" showTagline={false} />
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontSize: { xs: '1.125rem', sm: '1.5rem', md: '2.125rem' }, wordBreak: 'break-word' }}
            >
              시설 모니터링 - {pool?.name || poolCode}
            </Typography>
          </Box>

          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="실시간 모니터링" {...hiddenTabProps(monitorTabOn(0))} />
              <Tab label="CCTV" {...hiddenTabProps(monitorTabOn(1))} />
              <Tab label="수질 정보" {...hiddenTabProps(monitorTabOn(2))} />
              <Tab label="안전 상태" {...hiddenTabProps(monitorTabOn(3))} />
              <Tab label="센서" {...hiddenTabProps(monitorTabOn(4))} />
              <Tab label="알림" {...hiddenTabProps(monitorTabOn(5))} />
              <Tab label="웨어러블" {...hiddenTabProps(monitorTabOn(6))} />
              <Tab label="환경 설정" {...hiddenTabProps(monitorTabOn(7))} />
            </Tabs>
          </Paper>

      {/* 실시간 모니터링 탭 */}
      <TabPanel value={currentTab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>실시간 상태</Typography>
                  {pool && (
                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PoolIcon sx={{ color: 'primary.main', mr: 2, fontSize: 40 }} />
                              <Box>
                                <Typography variant="body2" color="text.secondary">시설 상태</Typography>
                                <Typography variant="h6">
                                  {pool.status === 'active' ? '운영 중' : 
                                   pool.status === 'maintenance' ? '유지보수 중' : '비활성화'}
              </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PersonIcon sx={{ color: 'primary.main', mr: 2, fontSize: 40 }} />
                              <Box>
                                <Typography variant="body2" color="text.secondary">현재 이용 인원</Typography>
                                <Typography variant="h6">
                                  N/A / {pool.capacity} 명 {/* Placeholder */}
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                      </Grid>

                      <Typography variant="subtitle1" sx={{ mt: 3, mb: 2 }}>수질 상태</Typography>
              <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <WaterDropIcon sx={{ color: 'info.main', mr: 1 }} />
                              <Box>
                                <Typography variant="body2" color="text.secondary">염소</Typography>
                                <Typography variant="h6">
                                  {waterQuality?.chlorine || waterQuality?.chlorine_level || 0} ppm
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <SpeedIcon sx={{ color: 'info.main', mr: 1 }} />
                              <Box>
                                <Typography variant="body2" color="text.secondary">pH</Typography>
                                <Typography variant="h6">
                                  {waterQuality?.ph || waterQuality?.ph_level || 0}
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <ThermostatIcon sx={{ color: 'info.main', mr: 1 }} />
                              <Box>
                                <Typography variant="body2" color="text.secondary">수온</Typography>
                                <Typography variant="h6">
                                  {waterQuality?.temperature || 0}°C
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <OpacityIcon sx={{ color: 'info.main', mr: 1 }} />
                              <Box>
                                <Typography variant="body2" color="text.secondary">탁도</Typography>
                                <Typography variant="h6">
                                  {waterQuality?.turbidity || 0} NTU
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                      </Grid>

                      <Typography variant="subtitle1" sx={{ mt: 3, mb: 2 }}>안전 상태</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <SecurityIcon sx={{ color: 'success.main', mr: 1 }} />
                              <Box>
                                <Typography variant="body2" color="text.secondary">안전요원</Typography>
                                <Typography variant="h6">
                                  {safetyStatus?.lifeguard_count || 0} / {safetyStatus?.requiredLifeguards || 2} 명 {/* Corrected property name */}
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <HealthAndSafetyIcon sx={{ color: 'success.main', mr: 1 }} />
                              <Box>
                                <Typography variant="body2" color="text.secondary">종합 안전 상태</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="h6" sx={{ mr: 1 }}>
                                    {safetyStatus?.is_safe ? '안전' : '주의 필요'}
                                  </Typography>
                                  {safetyStatus?.is_safe ? 
                                    <CheckCircleIcon color="success" /> : 
                                    <WarningIcon color="warning" />
                                  }
                                </Box>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>주요 지표</Typography>
                  {pool && (
                    <Box sx={{ mt: 2 }}>
                      <Card sx={{ mb: 2, p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                        <Typography variant="h4" sx={{ mb: 1 }}>
                          {/* Remove access to non-existent property */} 
                          {/* {safetyStatus?.current_occupancy || 0} */} 
                          N/A {/* Placeholder */} 
                        </Typography>
                        <Typography variant="body2">현재 이용자 수</Typography>
                        <Box sx={{ mt: 1, height: 4, width: '100%', bgcolor: 'primary.contrastText', borderRadius: 2 }}>
                      <Box
                        sx={{
                          height: '100%',
                              // Use crowd_level or other available metric if possible, or fixed value
                              width: `${Math.min(((safetyStatus?.crowd_level || 0) / 100) * 100, 100)}%`, // Example using crowd_level 
                              bgcolor: 'primary.dark',
                              borderRadius: 2
                            }} 
                          />
                        </Box>
                        <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                          최대 수용 인원 {pool.capacity}명
                        </Typography>
                      </Card>

                      <Card sx={{ mb: 2, p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}> 
                        <Typography variant="h4" sx={{ mb: 1 }}>
                          0 {/* Default */} 
                        </Typography>
                        <Typography variant="body2">오늘의 사고 발생 건수</Typography>
                      </Card>

                      <Card sx={{ mb: 2, p: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>활성 센서</Typography>
                        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
                          {sensors.filter(s => s.status === 'active').length}
                          <Typography component="span" variant="body2" sx={{ ml: 1 }}>/ {sensors.length}</Typography>
                        </Typography>
                      </Card>

                      <Card sx={{ p: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>활성 웨어러블</Typography>
                        <Typography variant="h4">
                          {pool?.wearables?.filter(w => w.status === 'active').length || 0}
                          <Typography component="span" variant="body2" sx={{ ml: 1 }}>/ {pool?.wearables?.length || 0}</Typography>
                        </Typography>
                      </Card>
                      </Box>
                  )}
                    </Paper>
                  </Grid>
              
              <Grid item xs={12} md={5}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>최근 알림</Typography>
                  {alerts.length > 0 ? (
                    <List sx={{ mt: 1 }}>
                      {alerts.slice(0, 5).map((alert) => (
                        <ListItem key={alert.id} sx={{ px: 1, borderLeft: 3, borderColor: alert.status === 'active' ? 'error.main' : alert.status === 'acknowledged' ? 'warning.main' : 'success.main', mb: 1, bgcolor: 'background.paper' }}>
                          <ListItemIcon>
                            {alert.status === 'active' ? (
                              <ErrorIcon color="error" />
                            ) : alert.status === 'acknowledged' ? (
                              <WarningIcon color="warning" />
                            ) : (
                              <CheckCircleIcon color="success" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={alert.title || alert.message}
                            secondary={`${new Date(alert.created_at).toLocaleString()} | ${alert.type === 'water_quality' ? '수질' : alert.type === 'safety' ? '안전' : alert.type === 'equipment' ? '장비' : '시스템'}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                      <Typography color="text.secondary">활성화된 알림 없음.</Typography>
                    </Box>
                  )}
            </Paper>
          </Grid>
              
              <Grid item xs={12} md={7}>
                <Paper sx={{ p: 3, height: '100%', minHeight: 420 }}>
                  <EventLogWidget
                    maxItems={10}
                    title="이벤트 내역"
                    subtitle="해당 시설의 최근 이벤트"
                    poolId={pool?.id != null ? String(pool.id) : undefined}
                    showRefresh={true}
                    showFilter={true}
                    showViewAll={false}
                    listMaxHeight={520}
                  />
                </Paper>
          </Grid>
              {pool?.id != null && Number.isFinite(Number(pool.id)) ? (
                <Grid item xs={12}>
                  <SafetyAlertDispatchHistoryCard
                    poolId={Number(pool.id)}
                    poolName={pool.name}
                    showPoolColumn={false}
                    title="현장 안전 알림 발송 이력"
                    subheader="SMS · 카카오 알림톡 · 웨어러블 — 이 시설 발송 결과"
                  />
                </Grid>
              ) : null}
        </Grid>
      </TabPanel>

          {/* CCTV 탭 */}
          <TabPanel value={currentTab} index={1}>
            {pool && (
              <RealCCTVTab 
                key={`cctv-${pool.id}`}
                poolId={Number(pool.id)} 
                useRemotePlaybackOverride={siteMonitorFlags.useRemotePlayback}
                siteGatewayEnabled={siteMonitorFlags.siteGatewayEnabled}
                preferLocalRtspSnapshot={siteMonitorFlags.preferLocalRtspSnapshot}
              />
            )}
          </TabPanel>

          {/* 수질 정보 탭 */}
          <TabPanel value={currentTab} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>현재 수질 상태</Typography>
                  {!waterQualityLoaded ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : waterQuality ? (
                    <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <WaterDropIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="body1">
                              <strong>염소 수치:</strong> {waterQuality.chlorine || waterQuality.chlorine_level} ppm
                            </Typography>
                          </Box>
                  </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <SpeedIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="body1">
                              <strong>pH 수치:</strong> {waterQuality.ph || waterQuality.ph_level}
                            </Typography>
                          </Box>
                  </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <ThermostatIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="body1">
                              <strong>수온:</strong> {waterQuality.temperature}°C
                            </Typography>
                          </Box>
                  </Grid>
                  <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PoolIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="body1">
                              <strong>탁도:</strong> {waterQuality.turbidity} NTU
                    </Typography>
                          </Box>
                  </Grid>
                </Grid>
                <Button
                  variant="contained"
                        startIcon={<RefreshIcon />}
                        sx={{ mt: 3 }}
                        onClick={() => {
                          if (pool) {
                            void fetchWaterQuality(Number(pool.id));
                          }
                        }}
                      >
                        새로고침
                </Button>
              </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        등록된 수질 정보 없음
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        sx={{ mt: 1.5 }}
                        onClick={() => {
                          if (pool) void fetchWaterQuality(Number(pool.id));
                        }}
                      >
                        다시 조회
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>수질 히스토리</Typography>
                  {waterQualityHistory.length > 0 ? (
                    <Box sx={{ width: '100%', height: 350 }}>
                      <Line
                        data={{
                          labels: waterQualityHistory.map(wq => 
                            new Date(wq.created_at).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })
                          ),
                          datasets: [
                            {
                              label: '염소 (ppm)',
                              data: waterQualityHistory.map(wq => wq.chlorine),
                              borderColor: 'rgba(54, 162, 235, 1)',
                              backgroundColor: 'rgba(54, 162, 235, 0.2)',
                              tension: 0.4
                            },
                            {
                              label: 'pH',
                              data: waterQualityHistory.map(wq => wq.ph),
                              borderColor: 'rgba(255, 99, 132, 1)',
                              backgroundColor: 'rgba(255, 99, 132, 0.2)',
                              tension: 0.4
                            },
                            {
                              label: '수온 (°C)',
                              data: waterQualityHistory.map(wq => wq.temperature),
                              borderColor: 'rgba(255, 206, 86, 1)',
                              backgroundColor: 'rgba(255, 206, 86, 0.2)',
                              tension: 0.4
                            },
                            {
                              label: '탁도 (NTU)',
                              data: waterQualityHistory.map(wq => wq.turbidity),
                              borderColor: 'rgba(75, 192, 192, 1)',
                              backgroundColor: 'rgba(75, 192, 192, 0.2)',
                              tension: 0.4
                            }
                          ]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'top',
                            },
                            title: {
                              display: true,
                              text: '최근 7일간 수질 변화'
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: false
                            }
                          }
                        }}
                      />
                    </Box>
                  ) : !waterQualityHistoryLoaded ? (
                    <Box sx={{ width: '100%', height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Box sx={{ width: '100%', height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        수질 히스토리 없음
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>수질 관리 권장사항</Typography>
                  <List>
                    {!waterQualityLoaded ? null : !waterQuality ? (
                      <ListItem>
                        <ListItemText
                          primary="권장사항 없음"
                          secondary="수질 측정 데이터가 없어 권장사항을 표시할 수 없음"
                        />
                      </ListItem>
                    ) : null}
                    {waterQuality && (
                      <>
                        {(waterQuality.chlorine || waterQuality.chlorine_level) < 1.0 && (
                          <ListItem>
                            <ListItemIcon>
                              <WarningIcon color="warning" />
                            </ListItemIcon>
                            <ListItemText
                              primary="염소 수치 낮음"
                              secondary="권장 염소 수치는 1.0~3.0ppm. 염소 수치를 높이기 위해 염소 투입을 검토."
                            />
                          </ListItem>
                        )}
                        {(waterQuality.ph || waterQuality.ph_level) < 7.2 && (
                          <ListItem>
                            <ListItemIcon>
                              <WarningIcon color="warning" />
                            </ListItemIcon>
                            <ListItemText
                              primary="pH 수치 낮음"
                              secondary="권장 pH 수치는 7.2~7.8. pH 수치를 높이기 위해 소다회 투입을 검토."
                            />
                          </ListItem>
                        )}
                        {(waterQuality.ph || waterQuality.ph_level) > 7.8 && (
                          <ListItem>
                            <ListItemIcon>
                              <WarningIcon color="warning" />
                            </ListItemIcon>
                            <ListItemText
                              primary="pH 수치 높음"
                              secondary="권장 pH 수치는 7.2~7.8. pH 수치를 낮추기 위해 산 투입을 검토."
                            />
                          </ListItem>
                        )}
                        {waterQuality.turbidity > 1.0 && (
                          <ListItem>
                            <ListItemIcon>
                              <WarningIcon color="warning" />
                            </ListItemIcon>
                            <ListItemText
                              primary="탁도 높음"
                              secondary="권장 탁도는 0.5 NTU 이하. 필터 청소 또는 응집제 사용을 검토."
                            />
                          </ListItem>
                        )}
                        {((waterQuality.chlorine || waterQuality.chlorine_level) >= 1.0 && 
                          (waterQuality.chlorine || waterQuality.chlorine_level) <= 3.0 && 
                          (waterQuality.ph || waterQuality.ph_level) >= 7.2 && 
                          (waterQuality.ph || waterQuality.ph_level) <= 7.8 && 
                          waterQuality.turbidity <= 1.0) && (
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText
                              primary="수질 상태가 양호"
                              secondary="모든 수질 수치가 권장 범위 내에 있음. 정기적인 점검을 계속 진행."
                            />
                          </ListItem>
                        )}
                      </>
                    )}
                  </List>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

          {/* 안전 상태 탭 */}
          <TabPanel value={currentTab} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>안전 점검 상태</Typography>
                  {!safetyStatusLoaded ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : safetyStatus ? (
                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={2}>
                        {/* 안전 점검 상태 카드 */}
                        <Grid item xs={12}>
                          <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardContent sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2">안전 점검 현황</Typography>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    if (pool) void fetchSafetyStatus(Number(pool.id));
                                  }}
                                >
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="body2">
                                      <strong>안전요원 배치 상태:</strong> {
                                        (safetyStatus.lifeguard_count ?? 0) >= (safetyStatus.requiredLifeguards || 2) 
                                          ? "적정" 
                                          : "부족"
                                      }
                                    </Typography>
                                    {(safetyStatus.lifeguard_count ?? 0) >= (safetyStatus.requiredLifeguards || 2) 
                                      ? <CheckCircleIcon color="success" sx={{ ml: 1 }} /> 
                                      : <WarningIcon color="error" sx={{ ml: 1 }} />
                                    }
                                  </Box>
                                </Grid>
                                <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="body2">
                                      <strong>현재 인원:</strong> N/A / {pool?.capacity || 100} 명
                                    </Typography>
                                    <CheckCircleIcon color="success" sx={{ ml: 1 }} />
                                  </Box>
                                </Grid>
                                <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="body2">
                                      <strong>혼잡도:</strong> {safetyStatus.crowd_level ?? 'N/A'}
                                    </Typography>
                                    {(safetyStatus.crowd_level ?? 0) < 30 
                                      ? <CheckCircleIcon color="success" sx={{ ml: 1 }} /> 
                                      : (safetyStatus.crowd_level ?? 0) < 70 
                                        ? <CheckCircleIcon color="success" sx={{ ml: 1 }} />
                                        : <WarningIcon color="error" sx={{ ml: 1 }} />
                                    }
                                  </Box>
                                </Grid>
                                <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="body2">
                                      <strong>안전 장비 상태:</strong> 확인 필요 
                                    </Typography>
                                    <CheckCircleIcon color="success" sx={{ ml: 1 }} />
                                  </Box>
                                </Grid>
                                <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="body2">
                                      <strong>최근 사고 발생 수:</strong> 0 건
                                    </Typography>
                                    <CheckCircleIcon color="success" sx={{ ml: 1 }} />
                                  </Box>
                                </Grid>
                                <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2">
                                      <strong>종합 안전 상태:</strong> {
                                        safetyStatus.is_safe === true ? '안전' : '주의 필요' 
                                      }
                                    </Typography>
                                    {(safetyStatus.is_safe === true) 
                                      ? <CheckCircleIcon color="success" sx={{ ml: 1 }} /> 
                                      : <WarningIcon color="error" sx={{ ml: 1 }} />
                                    }
                                  </Box>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* 실시간 알림 카드 */}
                        <Grid item xs={12}>
                          <Card variant="outlined">
                            <CardContent sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2">실시간 알림</Typography>
                                <IconButton size="small" color="primary" onClick={() => {
                                  if (pool) {
                                    axios.get(`/api/pools/${pool.id}/alerts`)
                                      .then(response => {
                                        if (response.data.success && response.data.data) {
                                          setAlerts(response.data.data);
                                          setSnackbar({
                                            open: true,
                                            message: '알림 정보가 업데이트 완료',
                                            severity: 'success'
                                          });
                                        }
                                      })
                                      .catch(error => {
                                        console.error(`Error fetching alerts for pool ${pool.id}:`, error);
                                        setSnackbar({
                                          open: true,
                                          message: '알림 정보 업데이트에 실패',
                                          severity: 'error'
                                        });
                                      });
                                  }
                                }}>
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <List dense sx={{ mt: 1 }}>
                                {alerts.length > 0 ? alerts.slice(0, 3).map((alert, index) => (
                                  <ListItem key={index} sx={{ px: 1, py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      {alert.priority === 'high' || alert.status === 'active' ? (
                                        <ErrorIcon fontSize="small" color="error" />
                                      ) : (alert.priority === 'medium' || alert.status === 'acknowledged') ? (
                                        <WarningIcon fontSize="small" color="warning" />
                                      ) : (
                                        <InfoIcon fontSize="small" color="info" />
                                      )}
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={alert.message}
                                      secondary={new Date(alert.created_at).toLocaleTimeString()}
                                      primaryTypographyProps={{ variant: 'body2' }}
                                      secondaryTypographyProps={{ variant: 'caption' }}
                                    />
                                  </ListItem>
                                )) : (
                                  <Box sx={{ textAlign: 'center', py: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      새로운 알림 없음
                                    </Typography>
                                  </Box>
                                )}
                              </List>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        등록된 안전 상태 정보 없음
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        sx={{ mt: 1.5 }}
                        onClick={() => {
                          if (pool) void fetchSafetyStatus(Number(pool.id));
                        }}
                      >
                        다시 조회
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>실시간 안전 모니터링</Typography>
                  {!safetyStatusLoaded ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : pool && safetyStatus ? (
                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={2}>
                        {/* CCTV 썸네일 */}
                        <Grid item xs={12}>
                          <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardContent sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2">안전 모니터링 CCTV</Typography>
                                <IconButton size="small" color="primary" onClick={fetchCctvs}>
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              {cctvLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                  <CircularProgress size={24} />
                                </Box>
                              ) : cctvError ? (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                  <Typography color="error">{cctvError}</Typography>
                                </Box>
                              ) : cctvs.filter((cctv) =>
                                matchCctvPurpose(cctv.purpose, CCTV_PURPOSE_SAFETY)
                              ).length > 0 ? (
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {cctvs.filter((cctv) =>
                                    matchCctvPurpose(cctv.purpose, CCTV_PURPOSE_SAFETY)
                                  ).map((cctv) => (
                                    <SafetyCctvTile
                                      key={cctv.id}
                                      cctv={cctv}
                                      useRemotePlayback={siteMonitorFlags.useRemotePlayback}
                                      siteGatewayEnabled={siteMonitorFlags.siteGatewayEnabled}
                                      centralGo2rtcAvailable={centralGo2rtcAvailable}
                                      centralGo2rtcStreams={centralGo2rtcStreams}
                                      preferLocalRtspSnapshot={
                                        siteMonitorFlags.preferLocalRtspSnapshot
                                      }
                                    />
                                  ))}
                                </Box>
                              ) : (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                  <Typography color="text.secondary">
                                    안전 모니터링 용도로 등록된 CCTV가 없음
                                  </Typography>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* 관리 모니터링 CCTV 섹션 */}
                        <Grid item xs={12}>
                          <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardContent sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2">관리 모니터링 CCTV</Typography>
                                <IconButton size="small" color="primary" onClick={fetchCctvs}>
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              {cctvLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                  <CircularProgress size={24} />
                                </Box>
                              ) : cctvError ? (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                  <Typography color="error">{cctvError}</Typography>
                                </Box>
                              ) : cctvs.filter((cctv) =>
                                matchCctvPurpose(cctv.purpose, CCTV_PURPOSE_MANAGEMENT)
                              ).length > 0 ? (
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {cctvs.filter((cctv) =>
                                    matchCctvPurpose(cctv.purpose, CCTV_PURPOSE_MANAGEMENT)
                                  ).map((cctv) => (
                                    <SafetyCctvTile
                                      key={cctv.id}
                                      cctv={cctv}
                                      useRemotePlayback={siteMonitorFlags.useRemotePlayback}
                                      siteGatewayEnabled={siteMonitorFlags.siteGatewayEnabled}
                                      centralGo2rtcAvailable={centralGo2rtcAvailable}
                                      centralGo2rtcStreams={centralGo2rtcStreams}
                                      preferLocalRtspSnapshot={
                                        siteMonitorFlags.preferLocalRtspSnapshot
                                      }
                                    />
                                  ))}
                                </Box>
                              ) : (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                  <Typography color="text.secondary">
                                    관리 모니터링 용도로 등록된 CCTV가 없음
                                  </Typography>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* 기타 모니터링 CCTV 섹션 */}
                        <Grid item xs={12}>
                          <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardContent sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2">기타 모니터링 CCTV</Typography>
                                <IconButton size="small" color="primary" onClick={fetchCctvs}>
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              {cctvLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                  <CircularProgress size={24} />
                                </Box>
                              ) : cctvError ? (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                  <Typography color="error">{cctvError}</Typography>
                                </Box>
                              ) : cctvs.filter((cctv) =>
                                matchCctvPurpose(cctv.purpose, CCTV_PURPOSE_OTHER)
                              ).length > 0 ? (
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {cctvs.filter((cctv) =>
                                    matchCctvPurpose(cctv.purpose, CCTV_PURPOSE_OTHER)
                                  ).map((cctv) => (
                                    <SafetyCctvTile
                                      key={cctv.id}
                                      cctv={cctv}
                                      useRemotePlayback={siteMonitorFlags.useRemotePlayback}
                                      siteGatewayEnabled={siteMonitorFlags.siteGatewayEnabled}
                                      centralGo2rtcAvailable={centralGo2rtcAvailable}
                                      centralGo2rtcStreams={centralGo2rtcStreams}
                                      preferLocalRtspSnapshot={
                                        siteMonitorFlags.preferLocalRtspSnapshot
                                      }
                                    />
                                  ))}
                                </Box>
                              ) : (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                  <Typography color="text.secondary">
                                    기타 모니터링 용도로 등록된 CCTV가 없음
                                  </Typography>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                        
                        {/* 실시간 안전 상태 지표 */}
                        <Grid item xs={12}>
                          <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardContent>
                              <Typography variant="subtitle2" gutterBottom>실시간 사용자 동태</Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Box>
                                  <Typography variant="body2" color="text.secondary">현재 시설 지역</Typography>
                                  <Typography variant="h5">N/A</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="body2" color="text.secondary">탈의실/샤워실</Typography>
                                  <Typography variant="h5">N/A</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="body2" color="text.secondary">총 인원</Typography>
                                  <Typography variant="h5">N/A</Typography>
                                </Box>
                              </Box>
                              {/* 시간대별 사용자 그래프 */}
                              <Box sx={{ mt: 2, height: 100 }}>
                                <Line
                                  data={{
                                    labels: safetyHistory.map(s => 
                                      new Date(s.created_at).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })
                                    ),
                                    datasets: [
                                      {
                                        label: '혼잡도',
                                        data: safetyHistory.map(s => s.crowd_level),
                                        borderColor: 'rgba(75, 192, 192, 1)',
                                        backgroundColor: 'rgba(75, 192, 192, 0.2)',
                                        fill: true,
                                        tension: 0.4
                                      }
                                    ]
                                  }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      legend: {
                                        display: false
                                      }
                                    },
                                    scales: {
                                      x: {
                                        display: false
                                      },
                                      y: {
                                        display: false,
                                        beginAtZero: true
                                      }
                                    },
                                    elements: {
                                      point: {
                                        radius: 0
                                      }
                                    }
                                  }}
                                />
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        등록된 안전 상태 정보 없음
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        sx={{ mt: 1.5 }}
                        onClick={() => {
                          if (pool) void fetchSafetyStatus(Number(pool.id));
                        }}
                      >
                        다시 조회
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          {/* 센서 탭 */}
          <TabPanel value={currentTab} index={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>센서 목록</Typography>
                  {sensors.length > 0 ? (
                    <List>
                    {sensors.map((sensor) => (
                        <ListItem 
                        key={sensor.id}
                          button 
                          onClick={() => handleSensorClick(sensor)}
                        selected={selectedSensor?.id === sensor.id}
                          sx={{
                            borderLeft: 3,
                            borderColor: selectedSensor?.id === sensor.id ? 'primary.main' : 'transparent',
                            mb: 1,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <ListItemIcon>
                            {sensor.type === 'temperature' ? (
                              <ThermostatIcon color="primary" />
                            ) : sensor.type === 'ph' ? (
                              <SpeedIcon color="primary" />
                            ) : sensor.type === 'chlorine' ? (
                              <WaterDropIcon color="primary" />
                            ) : sensor.type === 'turbidity' ? (
                              <OpacityIcon color="primary" />
                            ) : (
                              <BarChartIcon color="primary" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={sensor.name}
                            secondary={
                              <>
                                <Typography variant="body2" component="span" color="text.secondary">
                                  {sensor.location} · {sensor.status === 'active' ? '활성' : '비활성'}
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                          <Chip
                                    label={`최근: ${sensor.last_reading} ${sensor.last_reading_unit}`}
                            size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                </Box>
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
                      <Typography color="text.secondary">등록된 센서 없음.</Typography>
                    </Box>
                  )}
            </Paper>
          </Grid>
              
          <Grid item xs={12} md={8}>
            {selectedSensor ? (
                  <Paper sx={{ p: 3, height: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">{selectedSensor.name} 상세 정보</Typography>
                      <Button 
                        startIcon={<RefreshIcon />}
                        onClick={() => {
                          // 센서 데이터 새로고침
                          if (pool && selectedSensor) {
                            axios.get(`/api/sensors/${selectedSensor.id}/data`)
                              .then(response => {
                                if (response.data.success && response.data.data) {
                                  const dataArray = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
                                  setSensorData(dataArray);
                                  setSnackbar({
                                    open: true,
                                    message: '센서 데이터가 업데이트 완료',
                                    severity: 'success'
                                  });
                                }
                              })
                              .catch(error => {
                                console.error(`Error fetching sensor data for sensor ${selectedSensor.id}:`, error);
                                setSnackbar({
                                  open: true,
                                  message: '센서 데이터 업데이트에 실패',
                                  severity: 'error'
                                });
                              });
                          }
                        }}
                      >
                        새로고침
                      </Button>
                </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" gutterBottom>센서 정보</Typography>
                          <Typography variant="body2"><strong>ID:</strong> {selectedSensor.id}</Typography>
                          <Typography variant="body2"><strong>유형:</strong> {selectedSensor.type}</Typography>
                          <Typography variant="body2"><strong>위치:</strong> {selectedSensor.location}</Typography>
                          <Typography variant="body2"><strong>상태:</strong> {selectedSensor.status}</Typography>
                          <Typography variant="body2">
                            <strong>마지막 측정값:</strong> {selectedSensor.last_reading} {selectedSensor.last_reading_unit}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="subtitle1" gutterBottom>최신 수치</Typography>
                          <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'background.default', mb: 2 }}>
                            <Typography variant="h4" align="center" color="primary">
                              {selectedSensor.last_reading} <Typography component="span" variant="body2">{selectedSensor.last_reading_unit}</Typography>
                            </Typography>
                            <Typography variant="caption" display="block" align="center" color="text.secondary">
                              마지막 업데이트: {new Date(selectedSensor.updated_at).toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle1" gutterBottom>최근 측정 데이터</Typography>
                          {sensorData.length > 0 ? (
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                              <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>시간</TableCell>
                                    <TableCell>측정값</TableCell>
                        <TableCell>단위</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                                  {sensorData.map((data, index) => (
                                    <TableRow key={index}>
                          <TableCell>{new Date(data.timestamp).toLocaleString()}</TableCell>
                          <TableCell>{data.value}</TableCell>
                          <TableCell>{data.unit}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                              <Typography color="text.secondary">측정 데이터 없음.</Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                ) : (
                  <Paper sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" gutterBottom>
                        왼쪽에서 센서를 선택하면 상세 정보가 여기에 표시됨.
                      </Typography>
                    </Box>
                  </Paper>
                )}
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={currentTab} index={5}>
            <PoolMonitoringAlertsTab
              pool={pool}
              alerts={alerts}
              setAlerts={setAlerts}
              setSnackbar={setSnackbar}
            />
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12}>
                <EventLogWidget
                  maxItems={20}
                  title="이벤트 알림"
                  subtitle="이 시설의 위험·주의 이벤트 (실시간)"
                  poolId={pool?.id != null ? String(pool.id) : undefined}
                  showRefresh
                  showFilter
                  showViewAll={false}
                  listMaxHeight={480}
                />
              </Grid>
              {pool?.id != null && Number.isFinite(Number(pool.id)) ? (
                <Grid item xs={12}>
                  <SafetyAlertDispatchHistoryCard
                    poolId={Number(pool.id)}
                    poolName={pool.name}
                    showPoolColumn={false}
                    title="현장 안전 알림 발송 이력"
                    subheader="SMS · 카카오 알림톡 · 웨어러블 — 이 시설 발송 결과"
                  />
                </Grid>
              ) : null}
            </Grid>
          </TabPanel>

          {/* 웨어러블 탭 */}
          <TabPanel value={currentTab} index={6}>
            <Box sx={{ height: 'calc(100vh - 250px)', minHeight: '600px' }}>
              {pool && (
                <WearableStatus poolId={Number(pool.id)} />
              )}
            </Box>
          </TabPanel>

          {/* 환경 설정 탭 */}
          <TabPanel value={currentTab} index={7}>
            {pool && poolCode && (
              <Box sx={{ width: '100%', height: 'calc(100vh - 250px)', minHeight: '600px' }}>
                <iframe
                  src={`/pool/${poolCode}/settings?hideTabs=true`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '4px'
                  }}
                  title="환경 설정"
                />
              </Box>
            )}
          </TabPanel>
          
          {/* 스낵바 설정 */}
      <Snackbar
        open={snackbar.open}
            autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
            <MuiAlert 
              elevation={6} 
              variant="filled" 
              onClose={() => setSnackbar({ ...snackbar, open: false })} 
              severity={snackbar.severity}
            >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      {/* 대화상자 구현 */}
      {dialogType === 'addCCTV' && (
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>CCTV 관리</DialogTitle>
          <DialogContent>
            <Box sx={{ p: 2 }}>
              {pool && (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    새 CCTV 추가
                  </Typography>
                  <form>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="CCTV 이름"
                          name="name"
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="위치"
                          name="location"
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="스트림 URL"
                          name="url"
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>상태</InputLabel>
                          <Select
                            label="상태"
                            name="status"
                            defaultValue="active"
                          >
                            <MenuItem value="active">활성</MenuItem>
                            <MenuItem value="inactive">비활성</MenuItem>
                            <MenuItem value="maintenance">유지보수 중</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </form>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>취소</Button>
            <Button variant="contained" color="primary">
              등록
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {dialogType === 'addWearable' && (
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>웨어러블 관리</DialogTitle>
          <DialogContent>
            <Box sx={{ p: 2 }}>
              {pool && (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    새 웨어러블 추가
                  </Typography>
                  <form>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="웨어러블 이름"
                          name="name"
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>타입</InputLabel>
                          <Select
                            label="타입"
                            name="type"
                            defaultValue="watch"
                          >
                            <MenuItem value="watch">시계</MenuItem>
                            <MenuItem value="band">밴드</MenuItem>
                            <MenuItem value="tag">태그</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>상태</InputLabel>
                          <Select
                            label="상태"
                            name="status"
                            defaultValue="active"
                          >
                            <MenuItem value="active">활성</MenuItem>
                            <MenuItem value="inactive">비활성</MenuItem>
                            <MenuItem value="maintenance">유지보수 중</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </form>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>취소</Button>
            <Button variant="contained" color="primary">
              등록
            </Button>
          </DialogActions>
        </Dialog>
      )}
        </>
      )}
    </Container>
  );
};

export default PoolMonitoring; 